import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Types
export interface ClientData {
  id?: number;
  user_id?: number;
  client_type: 'particulier' | 'societe';
  email: string;
  adresse: string;
  telephone: string;
  // Champs particulier
  civilite?: 'Mr' | 'Mme';
  nom?: string;
  prenom?: string;
  // Champs société
  siren?: string;
  raison_sociale?: string;
  tva_intra?: string;
  status?: 'active' | 'inactive';
  created_at?: string;
  updated_at?: string;
  profession?: string;
  show_in_directory?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: string[];
}

class ClientService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Récupérer tous les clients
  async getAllClients(): Promise<ClientData[]> {
    try {
      const response = await axios.get<ApiResponse<ClientData[]>>(
        `${API_BASE_URL}/clients`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.message || 'Erreur lors de la récupération des clients');
      }
    } catch (error: any) {
      console.error('Erreur getAllClients:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Erreur lors de la récupération des clients');
    }
  }

  // Récupérer un client par ID
  async getClientById(id: number): Promise<ClientData> {
    try {
      const response = await axios.get<ApiResponse<ClientData>>(
        `${API_BASE_URL}/clients/${id}`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Client introuvable');
      }
    } catch (error: any) {
      console.error('Erreur getClientById:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Erreur lors de la récupération du client');
    }
  }

  // Créer un nouveau client
  async createClient(clientData: Omit<ClientData, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<ClientData> {
    try {
      // Validation côté client
      this.validateClientData(clientData);

      const response = await axios.post<ApiResponse<ClientData>>(
        `${API_BASE_URL}/clients`,
        clientData,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Erreur lors de la création du client');
      }
    } catch (error: any) {
      console.error('Erreur createClient:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response?.data?.errors?.length > 0) {
        throw new Error(error.response.data.errors.join(', '));
      }
      throw new Error('Erreur lors de la création du client');
    }
  }

  // Mettre à jour un client
  async updateClient(id: number, clientData: Partial<ClientData>): Promise<ClientData> {
    try {
      // Validation côté client si des données complètes sont fournies
      if (clientData.client_type) {
        this.validateClientData(clientData as ClientData);
      }

      const response = await axios.put<ApiResponse<ClientData>>(
        `${API_BASE_URL}/clients/${id}`,
        clientData,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Erreur lors de la mise à jour du client');
      }
    } catch (error: any) {
      console.error('Erreur updateClient:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response?.data?.errors?.length > 0) {
        throw new Error(error.response.data.errors.join(', '));
      }
      throw new Error('Erreur lors de la mise à jour du client');
    }
  }

  // Supprimer un client
  async deleteClient(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<void>>(
        `${API_BASE_URL}/clients/${id}`,
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de la suppression du client');
      }
    } catch (error: any) {
      console.error('Erreur deleteClient:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Erreur lors de la suppression du client');
    }
  }

  // Validation des données client côté frontend
  private validateClientData(clientData: ClientData | Partial<ClientData>): void {
    const errors: string[] = [];

    // Champs obligatoires communs
    if (!clientData.email?.trim()) {
      errors.push('L\'email est obligatoire');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)) {
      errors.push('Format d\'email invalide');
    }

    if (!clientData.adresse?.trim()) {
      errors.push('L\'adresse est obligatoire');
    }

    if (!clientData.telephone?.trim()) {
      errors.push('Le téléphone est obligatoire');
    }

    // Validation selon le type
    if (clientData.client_type === 'particulier') {
      if (!clientData.civilite) {
        errors.push('La civilité est obligatoire pour un particulier');
      }
      if (!clientData.nom?.trim()) {
        errors.push('Le nom est obligatoire pour un particulier');
      }
      if (!clientData.prenom?.trim()) {
        errors.push('Le prénom est obligatoire pour un particulier');
      }
    } else if (clientData.client_type === 'societe') {
      if (!clientData.siren?.trim()) {
        errors.push('Le SIREN est obligatoire pour une société');
      }
      if (!clientData.raison_sociale?.trim()) {
        errors.push('La raison sociale est obligatoire pour une société');
      }
      if (!clientData.tva_intra?.trim()) {
        errors.push('La TVA intracommunautaire est obligatoire pour une société');
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }
  }

  // Méthode utilitaire pour formater les données client pour l'affichage
  formatClientForDisplay(client: ClientData): string {
    if (client.client_type === 'particulier') {
      return `${client.civilite || ''} ${client.prenom || ''} ${client.nom || ''}`.trim();
    } else {
      return client.raison_sociale || '';
    }
  }
}

const clientService = new ClientService();
export default clientService;